/* global t, safeArray, num, nbToIso, isPostedInvoice, invoicePostingFactor, invoiceLineNet, invoiceLineVatTreatment, isActiveExpense, isActiveOtherSale, dashboardExpenseLineItems, dashboardExpenseDeductibleVatPercent, dashboardExpenseVatBreakdown */
// MVA-AVSTEMMING — transaksjonene bak avgiften
//
// Skilt fra backend-vat-recon-view.jsx, som eier hovedboken og sammenstillingen.
// Her utledes de enkelte salgs- og kjøpslinjene MVA-en faktisk består av, med de
// varslene dataene gir grunnlag for.
//
// Ingen av varslene er en gjetning på fritekst. Designets `uvanlig_kode`
// (uvanlig MVA-kode for konto) er IKKE portert: den forutsetter en forventet
// MVA-kode per konto, og kontoplanen i appen har ingen. Et varsel uten kilde er
// verre enn ingen varsel.
//
// Read-only. Ingen skriving, ingen Firebase.

// Datoen en post foeres paa, som ISO. Mangler den eller er den ubrukelig, er
// svaret `null` og ikke dagens dato: en post uten dato hoerer ikke til i en
// periode, og `nbToIso` sin fallback til i dag ville flyttet den inn i
// inneverende maaned uten at noen sa fra.
const vatReconIso = (value) => {
  const text = String(value || '').trim();
  if (!text) return null;
  const iso = /^\d{1,2}\.\d{1,2}\.\d{4}$/.test(text) ? nbToIso(text) : text.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : null;
};
const vatReconRound = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100;

const VAT_RECON_FLAG = {
  mangler_kode: { get label() { return t('avf.common.missingTreatment', 'Avgiftsbehandling ikke fastsatt'); }, cls: 'error' },
  ingen_fradrag: { get label() { return t('avf.common.noDeduction', 'Ingen fradragsrett'); }, cls: 'warning' },
  delvis_fradrag: { get label() { return t('avf.common.partialDeduction', 'Delvis fradragsrett'); }, cls: 'info' },
  omvendt: { get label() { return t('avf.common.reverseCharge', 'Omvendt avgiftsplikt'); }, cls: 'warning' },
  tolldok_mangler: { get label() { return t('avf.ov.tile.customsMissing', 'Tolldeklarasjon mangler'); }, cls: 'error' },
  direkte: { get label() { return t('avf.common.postedDirectly', 'Direkteført på MVA-konto'); }, cls: 'warning' },
  duplikat: { get label() { return t('rcv.flag.duplicate', 'Mulig duplikatbilag'); }, cls: 'error' },
  uten_mva_dok: { get label() { return t('rcv.flag.salesWithoutDoc', 'Salg uten MVA krever dokumentasjon'); }, cls: 'warning' },
  kreditnota: { get label() { return t('rcv.flag.creditNote', 'Kreditnota — kontroller periodisering'); }, cls: 'info' },
};

// MVA-koden en salgslinje rapporteres under. Kodene er de samme `deriveVat`
// bruker; utledningen ligger her fordi den skal kunne knyttes til ETT bilag,
// mens `deriveVat` bare summerer.
const vatReconSalesCode = (treatment, rate) => {
  if (treatment === 'standard') {
    const value = num(rate);
    if (value === 25) return '3';
    if (value === 15) return '31';
    if (value === 12) return '33';
    return '';
  }
  if (treatment === 'salesReverseCharge51') return '51';
  if (treatment === 'export52') return '52';
  if (treatment === 'zeroRated') return '5';
  return '6';
};

const vatReconPurchaseCode = (line) => {
  const reverse = String(line.reverseChargeCode || '');
  if (reverse === '81' || reverse === '86') return reverse;
  if (line.vatTreatment === 'vatOnly') return String(line.purchaseVatCode || '') === '14' ? '14' : '1';
  if (line.vatTreatment === 'importBasis') return String(line.standardVatCode || line.purchaseVatCode || '') === '23' ? '20' : String(line.standardVatCode || line.purchaseVatCode || '');
  const rate = num(line.vatRate);
  if (rate === 25) return '1';
  if (rate === 15) return '11';
  if (rate === 12) return '13';
  return rate === 0 ? '0' : '';
};

// To bilag med samme motpart, dato og beløp er et duplikat verdt å se på. Det
// er et forslag til gjennomgang, ikke en konklusjon: en leverandør kan sende to
// like fakturaer samme dag.
const vatReconDuplicateKey = (row) => [
  row.domain,
  String(row.motpart || '').trim().toLowerCase(),
  vatReconRound(row.grunnlag).toFixed(2),
  row.dato,
].join('|');

// Salg uten utgående MVA må kunne dokumenteres. Kravet er bokføringsforskriftens,
// ikke et varsel flaten har funnet på.
const VAT_RECON_EXEMPT_SALES_CODES = ['5', '6', '51', '52'];


// ------------------------------------------------------------------
// Transaksjonene bak avgiften
// ------------------------------------------------------------------

const vatReconSalesFlags = ({ treatment, rate, code, isCredit }) => {
  const flags = [];
  // «standard» betyr avgiftspliktig. Er satsen null, er behandlingen ikke
  // fastsatt — det er en reell motsigelse i bilaget, ikke en antakelse.
  if (!code || (treatment === 'standard' && num(rate) <= 0)) flags.push('mangler_kode');
  if (VAT_RECON_EXEMPT_SALES_CODES.includes(code)) flags.push('uten_mva_dok');
  if (isCredit) flags.push('kreditnota');
  return flags;
};

const vatReconSalesRows = (state, range) => {
  const rows = [];
  for (const invoice of safeArray(state.invoices)) {
    const date = vatReconIso(invoice.date);
    if (!date || date < range.from || date > range.to) continue;
    if (!isPostedInvoice(invoice, state)) continue;
    const factor = invoicePostingFactor(invoice);
    const isCredit = invoice.kind === 'credit';
    safeArray(invoice.lines).forEach((line, index) => {
      const amounts = invoice.accountingRoundingVersion === 'NO-VAT-BUCKET-1' ? invoice.accountingAmounts.lines[index] : null;
      const base = vatReconRound((amounts ? amounts.net : invoiceLineNet(line)) * factor);
      if (!base) return;
      const treatment = invoiceLineVatTreatment(line);
      const rate = num(line.vatRate);
      const code = vatReconSalesCode(treatment, rate);
      rows.push({
        id: 'salg:' + invoice.id + ':' + index,
        domain: 'salg',
        motpart: invoice.customer?.name || invoice.customerName || t('rcv.item.customer', 'Kunde'),
        dok: invoice.invoiceNumber || invoice.id,
        dato: date,
        konto: line.salesAccountCode || invoice.salesAccountCode || '3000',
        grunnlag: base,
        mvaKode: code,
        sats: treatment === 'standard' ? rate : null,
        mvaBelop: amounts ? vatReconRound(amounts.vat * factor) : treatment === 'standard' ? vatReconRound(base * rate / 100) : 0,
        utgMva: 0,
        fradragsgrad: null,
        land: '',
        importDok: '',
        flags: vatReconSalesFlags({ treatment, rate, code, isCredit }),
      });
    });
  }
  for (const sale of safeArray(state.otherSales)) {
    const date = vatReconIso(sale.date);
    if (!date || date < range.from || date > range.to) continue;
    if (!isActiveOtherSale(sale) || sale.vatTreatment === 'vatOnly') continue;
    const byRate = new Map();
    globalThis.PlummeOtherSaleAmounts.groups(sale).forEach((group, index) => {
      const base = group.amountNet, treatment = group.vatTreatment, rate = group.vatRate;
      const code = vatReconSalesCode(treatment, rate);
      // Cumulative rounding preserves VAT per rate even across several accounts.
      const prior = byRate.get(rate) || 0;
      const vat = treatment === 'standard' ? vatReconRound((prior + base) * rate / 100) - vatReconRound(prior * rate / 100) : 0;
      if (treatment === 'standard') byRate.set(rate, prior + base);
      rows.push({
        id: 'annetsalg:' + sale.id + ':' + index,
        domain: 'salg', motpart: sale.customerName || sale.description || t('rcv.item.otherSale', 'Annet salg'),
        dok: sale.voucherNumber || sale.id, dato: date, konto: group.salesAccountCode,
        grunnlag: base, mvaKode: code, sats: treatment === 'standard' ? rate : null,
        mvaBelop: vatReconRound(vat), utgMva: 0, fradragsgrad: null, land: '', importDok: '',
        flags: vatReconSalesFlags({ treatment, rate, code, isCredit: false }),
      });
    });
  }
  return rows;
};

// Tolldeklarasjonene i perioden, nøklet på leverandørnavn. En vareimport uten
// deklarasjon mangler grunnlaget for innførselsmerverdiavgiften.
const vatReconCustomsIndex = (state, range) => {
  const index = new Map();
  for (const entry of safeArray(state.customsDeclarations)) {
    if (entry?.status === 'annulled') continue;
    const date = vatReconIso(entry.date);
    if (!date || date < range.from || date > range.to) continue;
    const key = String(entry.supplierName || entry.description || '').trim().toLowerCase();
    if (key) index.set(key, entry);
  }
  return index;
};

// A user-entered source leg posted directly to a VAT return control account is
// never explained by a VAT code. It stays in the ledger, is excluded from the
// return, and must remain visible as a reconciliation warning. Workflow-owned
// supplier credits and reimbursements are reviewed in their owning flows.
const vatReconManualRows = (expense, date, motpart) => {
  const rows = [];
  if (expense.supplierCreditForExpenseId || expense.supplierCreditRefundForId || expense.privatePurchaseId) return rows;
  safeArray(expense.manualPostingLines).forEach((line, lineIndex) => {
    ['from', 'to'].forEach((side) => {
      const code = String(line[side + 'VatCode'] || '').trim();
      const account = String(line[side + 'Account'] || '').trim().match(/^(\d{4})/)?.[1] || '';
      if (!/^(?:270[0-3]|271[0-3])$/.test(account)) return;
      rows.push({
        id: 'manuell:' + expense.id + ':' + lineIndex + ':' + side,
        domain: 'kjop', motpart, dok: expense.voucherNumber || expense.id, dato: date,
        konto: account, grunnlag: null, mvaKode: code || 'none', sats: null,
        mvaBelop: vatReconRound(Math.abs(num(line[side + 'Amount']))), utgMva: 0,
        fradragsgrad: null, land: '', importDok: '', flags: ['direkte'],
      });
    });
  });
  return rows;
};

const vatReconPurchaseRows = (state, range, customsIndex) => {
  const rows = [];
  for (const expense of safeArray(state.expenses)) {
    const date = vatReconIso(expense.date);
    if (!date || date < range.from || date > range.to) continue;
    if (!isActiveExpense(expense)) continue;
    const motpart = expense.supplierName || expense.description || t('rcv.item.purchase', 'Kjøp');
    // The customs index is keyed on stored supplier names; the display fallback must not change the lookup.
    const customsKey = String(expense.supplierName || expense.description || 'Kjøp').trim().toLowerCase();

    if (expense.manualPosting && safeArray(expense.manualPostingLines).length) {
      rows.push(...vatReconManualRows(expense, date, motpart));
      continue;
    }

    const deductiblePercent = dashboardExpenseDeductibleVatPercent(expense);
    dashboardExpenseLineItems(expense).forEach((line, index) => {
      const lineAmount = vatReconRound(line.amountNet);
      const base = line.vatTreatment === 'vatOnly' ? 0 : lineAmount;
      if (!lineAmount) return;
      const code = vatReconPurchaseCode(line);
      const breakdown = dashboardExpenseVatBreakdown({
        ...expense, ...line, amountNet: base, purchaseLines: [],
      });
      const reverse = String(line.reverseChargeCode || '');
      const hasCustoms = customsIndex.has(customsKey);
      const flags = [];
      if (!code) flags.push('mangler_kode');
      if (deductiblePercent === 0) flags.push('ingen_fradrag');
      else if (deductiblePercent < 100) flags.push('delvis_fradrag');
      if (reverse === '81' || reverse === '86') flags.push('omvendt');
      // Vareimport uten tolldeklarasjon i perioden: innførselsgrunnlaget kan
      // ikke kontrolleres mot noe.
      if (reverse === '81' && !hasCustoms) flags.push('tolldok_mangler');
      rows.push({
        id: 'kjop:' + expense.id + ':' + index,
        domain: reverse === '81' || reverse === '86' ? 'utland' : 'kjop',
        motpart,
        dok: expense.voucherNumber || expense.id,
        dato: date,
        konto: expense.accountCode || expense.expenseAccountCode || expense.categoryAccountCode || '',
        grunnlag: base,
        mvaKode: code,
        sats: num(line.vatRate),
        mvaBelop: vatReconRound(breakdown.deductibleVat),
        utgMva: vatReconRound(breakdown.reverseChargeOutputVat),
        fradragsgrad: deductiblePercent,
        land: expense.supplierCountry || expense.country || '',
        importDok: reverse === '81' ? (hasCustoms ? 'Registrert' : 'Mangler') : 'Ikke relevant',
        flags,
      });
    });
  }
  return rows;
};

Object.assign(window, {
  VAT_RECON_FLAG, VAT_RECON_EXEMPT_SALES_CODES,
  vatReconSalesCode, vatReconPurchaseCode, vatReconDuplicateKey,
  vatReconSalesRows, vatReconPurchaseRows, vatReconCustomsIndex,
  vatReconIso, vatReconRound,
});
